/**
 * Routing, fetching and the two timing utilities the app needs. No router dependency: five screens
 * do not justify one, and `pushState` plus `popstate` is the whole mechanism.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { errorMessage } from "./api.ts";

export const ROUTES = {
	scan: "/",
	category: "/category",
	result: "/result",
	accounts: "/accounts",
	history: "/history",
	offers: "/offers",
} as const;

export type Route = keyof typeof ROUTES;

const ROUTE_BY_PATH: Record<string, Route> = Object.fromEntries(
	(Object.keys(ROUTES) as Route[]).map((route) => [ROUTES[route], route]),
);

export function navigate(route: Route): void {
	const path = ROUTES[route];
	if (window.location.pathname === path) return;
	window.history.pushState(null, "", path);
	// `pushState` is silent by design; the app listens on one event either way.
	window.dispatchEvent(new PopStateEvent("popstate"));
}

function subscribeRoute(onChange: () => void): () => void {
	window.addEventListener("popstate", onChange);
	return () => window.removeEventListener("popstate", onChange);
}

export function useRoute(): Route {
	return useSyncExternalStore(subscribeRoute, () => {
		const path = window.location.pathname;
		const trimmed = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
		return ROUTE_BY_PATH[trimmed] ?? "scan";
	});
}

export type Async<T> = {
	data: T | null;
	error: string | null;
	loading: boolean;
	reload: () => void;
};

/**
 * One in-flight request per hook instance, aborted when the dependencies change or the component
 * unmounts. The previous `data` is deliberately kept while a reload is in flight so the screen does
 * not blank out every time the basket ticks.
 */
export function useApi<T>(load: ((signal: AbortSignal) => Promise<T>) | null, deps: readonly unknown[]): Async<T> {
	const [data, setData] = useState<T | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(load !== null);
	const [nonce, setNonce] = useState(0);
	const loadRef = useRef(load);
	loadRef.current = load;

	useEffect(() => {
		const fn = loadRef.current;
		if (fn === null) {
			setLoading(false);
			return;
		}
		const controller = new AbortController();
		setLoading(true);
		setError(null);
		fn(controller.signal).then(
			(value) => {
				if (controller.signal.aborted) return;
				setData(value);
				setError(null);
				setLoading(false);
			},
			(err: unknown) => {
				// Only our own cancellation is silent. A transport AbortError is still a failure.
				if (controller.signal.aborted) return;
				setError(errorMessage(err));
				setLoading(false);
			},
		);
		return () => controller.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nonce, ...deps]);

	const reload = useCallback(() => setNonce((n) => n + 1), []);
	return { data, error, loading, reload };
}

/** Trailing-edge debounce. Used so a burst of scans produces one recommendation request. */
export function useDebounced<T>(value: T, ms: number): T {
	const [settled, setSettled] = useState(value);
	useEffect(() => {
		const timer = window.setTimeout(() => setSettled(value), ms);
		return () => window.clearTimeout(timer);
	}, [value, ms]);
	return settled;
}

type WakeLockSentinel = { released: boolean; release: () => Promise<void> };
type WakeLockApi = { request: (type: "screen") => Promise<WakeLockSentinel> };

/**
 * Holds the screen awake while the loyalty barcode is on it. Nothing on the web can raise the
 * hardware backlight, so the barcode screen pairs this with a full-white background instead.
 */
export function useScreenWakeLock(active: boolean): void {
	useEffect(() => {
		if (!active) return;
		const navigatorWithLock: { wakeLock?: WakeLockApi } = navigator;
		const api = navigatorWithLock.wakeLock;
		if (api === undefined) return;
		let sentinel: WakeLockSentinel | null = null;
		let cancelled = false;
		api.request("screen").then(
			(lock) => {
				if (cancelled) void lock.release();
				else sentinel = lock;
			},
			() => {
				/* denied while backgrounded; the barcode still renders */
			},
		);
		return () => {
			cancelled = true;
			if (sentinel !== null && !sentinel.released) void sentinel.release();
		};
	}, [active]);
}
